import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { toApiError } from './api-error';
import type { SessionRefresher } from './session-refresher';
import type { SessionStore } from './session-store';

const DEFAULT_TIMEOUT_MS = 15_000;
const UNAUTHORIZED = 401;

/** Opções do cliente HTTP da aplicação. */
export interface HttpClientOptions {
  baseURL: string;
  store: SessionStore;
  refresher: SessionRefresher;
  timeoutMs?: number;
}

type RetriableConfig = InternalAxiosRequestConfig & {
  retriedAfterRefresh?: boolean;
  sessionGeneration?: number;
};

/**
 * Cliente axios da aplicação: injeta o access token (em memória), trata 401 com refresh
 * single-flight e repete a requisição original uma única vez. 403 nunca dispara refresh.
 * Toda falha sai normalizada como ApiError.
 */
export function createHttpClient(options: HttpClientOptions): AxiosInstance {
  const client = axios.create({
    baseURL: options.baseURL,
    withCredentials: true,
    timeout: options.timeoutMs ?? DEFAULT_TIMEOUT_MS,
  });

  client.interceptors.request.use((config) => {
    const { accessToken, generation } = options.store.getState();
    (config as RetriableConfig).sessionGeneration ??= generation;
    if (accessToken !== null) config.headers.set('Authorization', `Bearer ${accessToken}`);
    else config.headers.delete('Authorization');
    return config;
  });

  client.interceptors.response.use(undefined, async (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined;
    if (
      error.response?.status !== UNAUTHORIZED ||
      config === undefined ||
      config.retriedAfterRefresh === true ||
      config.sessionGeneration !== options.store.getState().generation
    ) {
      throw toApiError(error);
    }
    const { accessToken } = options.store.getState();
    const hasNewToken =
      accessToken !== null && config.headers.get('Authorization') !== `Bearer ${accessToken}`;
    if (!hasNewToken) {
      const outcome = await options.refresher.refresh();
      if (outcome !== 'authenticated') throw toApiError(error);
    }
    if (config.sessionGeneration !== options.store.getState().generation) throw toApiError(error);
    config.retriedAfterRefresh = true;
    return client.request(config);
  });

  return client;
}
